<template>
  <fieldset v-if="!field.isHidden" :data-test="name" class="cap-form-fieldset" :class="{ inline }">
    <legend :id="'checkbox_label_' + name" v-html="legend"></legend>
    <label>
      <transition name="slide">
        <span v-if="isError" :id="'checkbox_error_hint_' + name" class="text-negative" v-html="$to(internalHint)"></span>
      </transition>
      <input
        ref="input"
        :checked="internalValue"
        type="checkbox"
        class="form-checkbox"
        v-bind="$attrs"
        :name="name"
        :disabled="isDisabled"
        :aria-labelledby="'checkbox_label_' + name"
        :aria-describedby="isError ? 'checkbox_error_hint_' + name : null"
        :aria-required="required"
        :required="labelAsRequired"
        :aria-invalid="isError ? true : null"
        @change="checkValue"
      />
      <slot />
    </label>
  </fieldset>
</template>

<script>
import { emptyFormElement } from '@/utils/form';

export default {
  name: 'FormCheckbox',

  inject: {
    parentForm: {
      from: 'parentForm',
      default: null,
    },
  },

  inheritAttrs: false,

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    name: {
      type: String,
      required: false,
      default: null,
    },

    label: {
      type: String,
      required: false,
      default: null,
    },

    hint: {
      type: String,
      required: false,
      default: null,
    },

    modelValue: {
      type: Boolean,
      required: false,
    },

    required: {
      type: Boolean,
      required: false,
    },

    labelAsRequired: {
      type: Boolean,
      required: false,
    },

    form: {
      type: Object,
      required: false,
      default: null,
    },

    inline: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      internalValue: this.modelValue,
      internalHint: this.hint,
    };
  },

  computed: {
    state() {
      if (this.parentForm) return this.parentForm.form;
      return this.form;
    },

    field() {
      if (!this.state || !this.name || !this.state[this.name]) return emptyFormElement();
      return this.state[this.name];
    },

    fieldHasFocus() {
      return this.field.hasFocus;
    },

    isError() {
      return !this.field.isValid && this.field.isChecked;
    },

    isDisabled() {
      return this.state.isDisabled;
    },

    legend() {
      if (this.label != null){
        if (this.required){
          return this.label + "<span style='color: red'>*</span>";
        } else {
          return this.label;
        }
      } else if (this.required){
        return "<span style='color: red'>*</span>";
      } else {
        return "";
      }
    },
  },

  watch: {
    fieldHasFocus: 'checkFocus',
  },

  mounted() {
    if (!this.field.isHidden) {
      this.defaultValue = this.internalValue;
      this.field.checkValidation = this.checkValue;
      this.field.resetValue = this.resetValue;
      this.field.setInvalid = this.setInvalid;
      this.$set(this.field, 'name', this.name);
      this.$nextTick(() => {
        this.checkValue();
        this.checkFocus();
        this.field.isInit = true;
        if (this.field.promise) this.field.resolve();
      });
    }
  },

  methods: {
    setInvalid(msg) {
      this.internalHint = msg || this.hint;
      this.field.isValid = false;
      this.field.isChecked = true;
    },

    checkFocus() {
      if (this.fieldHasFocus && this.$refs.input) {
        this.field.hasFocus = false;
        this.$refs.input.focus();
      }
    },

    checkValue() {
      this.internalValue = this.$refs.input?.checked;
      this.$set(this.field, 'isValid', !this.required || this.internalValue);
      this.$set(this.field, 'value', this.internalValue);
      this.$emit('update:modelValue', this.internalValue);
    },

    resetValue() {
      this.$refs.input.checked = this.defaultValue;
      this.checkValue();
    },
  },
};
</script>

<style scoped lang="scss">
.inline {
  float: left;
  margin-top: 4px;
}
</style>
